/**
 * FenzSafe P2P ("Path to Paid") funnel instrumentation (founder continuation directive, item 1).
 * Reuses the exact `window.gtag`/`window.dataLayer` mechanism the one real GA4 integration in this
 * account already uses (`carolina-server-website/analytics.js`) rather than inventing a second,
 * ad-hoc analytics system -- if/when a GA4 property is wired into this app (`apps/web`), these
 * calls start flowing immediately with no FenzSafe-side change. Today no GA4 script is loaded in
 * `apps/web` at all, so `trackFenzSafeFunnelEvent()` is a documented, safe no-op in production
 * until that script exists -- see docs/ai/FENZSAFE_P2P_FUNNEL_INSTRUMENTATION.md §1 for the exact
 * search performed across every repo this session can reach (profenz, profenz-public-website,
 * carolina-server-website, profenz-gemini-lab-v1) confirming no FenzSafe/ProFenz-specific GA4
 * *event taxonomy* exists yet to align with -- only that one bare pageview-only snippet, which its
 * own repo's AGENTS.md forbids linking to ProFenz at all. This module uses the founder's own literal
 * milestone vocabulary as the event names, so nothing here is invented once a taxonomy does exist.
 *
 * Privacy-minimized by construction, not by convention: `FenzSafeFunnelEvent` is a closed union of
 * milestone names, each with its own narrow, enum-only optional params. There is no free-text field
 * anywhere in this type, so evidence content, filenames, party/subject names, emails, phone numbers,
 * addresses, or case details cannot be passed through this seam even by caller error -- the type
 * system itself is the privacy boundary the founder's directive requires.
 */

export type FenzSafeFunnelMilestone =
  | 'organizer_started'
  | 'email_captured'
  | 'matter_created'
  | 'evidence_added'
  | 'first_value_reached'
  | 'organizer_completed'
  | 'attorney_shared'
  | 'attorney_opened'
  | 'attorney_signup_clicked'
  | 'paid_conversion'
  | 'profenz_transaction'
  | 'retained_user';

export type FenzSafeFunnelEvent =
  | {milestone: 'organizer_started'}
  | {milestone: 'email_captured'}
  | {milestone: 'matter_created'}
  | {milestone: 'evidence_added'; source: 'upload' | 'phone_import' | 'desktop_discovery'}
  | {milestone: 'first_value_reached'}
  | {milestone: 'organizer_completed'}
  | {milestone: 'attorney_shared'}
  | {milestone: 'attorney_opened'}
  | {milestone: 'attorney_signup_clicked'}
  | {milestone: 'paid_conversion'; tier: 'one_time' | 'monthly'}
  | {milestone: 'profenz_transaction'}
  | {milestone: 'retained_user'};

type Gtag = (...args: unknown[]) => void;

/**
 * Fires exactly one GA4 event named `fenzsafe_<milestone>`, with only the closed, enum-only fields
 * from `FenzSafeFunnelEvent` as params -- matching the existing GA4 pattern's own
 * `window.gtag`/`window.dataLayer` shim exactly (`carolina-server-website/analytics.js`), so this
 * degrades to a safe no-op wherever no GA4 script has installed `window.gtag` (true in `apps/web`
 * today) and requires no change here once one is. Never throws: a broken or absent analytics
 * pipe must never break the product it's instrumenting.
 */
export function trackFenzSafeFunnelEvent(event: FenzSafeFunnelEvent): void {
  try {
    const gtag = (window as unknown as {gtag?: Gtag}).gtag;
    if (typeof gtag !== 'function') return;
    const {milestone, ...params} = event;
    gtag('event', `fenzsafe_${milestone}`, params);
  } catch {
    // Analytics must never break the product it's instrumenting.
  }
}

const RETURNING_VISITOR_KEY = 'fenzsafe_funnel_returning_visitor';
const SESSION_RETENTION_FIRED_KEY = 'fenzsafe_funnel_retention_fired_this_session';
const GUEST_STARTED_KEY = 'fenzsafe_funnel_guest_started';

/**
 * The top-of-funnel signal for the guest-first, unauthenticated entry point
 * (FenzSafeGuestEntry.tsx, Issue #192 Docket #19). Unlike trackFenzSafeEntry() below, a guest visit
 * has not captured an email, so this fires organizer_started alone -- never bundled with
 * email_captured -- at most once per browser (localStorage-guarded; degrades to firing every visit
 * if storage is unavailable, the safe direction for a one-shot top-of-funnel signal).
 */
export function trackFenzSafeGuestEntry(): void {
  try {
    if (window.localStorage.getItem(GUEST_STARTED_KEY) === 'true') return;
    window.localStorage.setItem(GUEST_STARTED_KEY, 'true');
    trackFenzSafeFunnelEvent({milestone: 'organizer_started'});
  } catch {
    trackFenzSafeFunnelEvent({milestone: 'organizer_started'});
  }
}

/**
 * The one-time top-of-funnel signal for the real, authenticated FenzSafe surface
 * (FenzSafeWorkspace.tsx). Originally `organizer_started`/`email_captured` necessarily fired
 * together here -- see docs/ai/FENZSAFE_P2P_FUNNEL_INSTRUMENTATION.md §2 for why: `/fenzsafe` was
 * only ever reachable from Application.tsx after a session already existed, and a session couldn't
 * exist without the shared low-friction email-verification step already having completed, so that
 * was the earliest point FenzSafe-owned code could distinguish "reached FenzSafe for the first time
 * in this browser" at all. That assumption no longer holds now that the guest-first entry point
 * (FenzSafeGuestEntry.tsx, trackFenzSafeGuestEntry() above) can fire organizer_started first, with no
 * email yet: if this browser already has the guest-started marker but not the returning-visitor one,
 * this is that guest's real conversion moment, so only email_captured fires (not a duplicate
 * organizer_started). Otherwise, a genuinely first-ever authenticated visit (no guest step first --
 * e.g. a direct signup) still fires both together, and a repeat visit fires `retained_user` instead,
 * at most once per browser tab session (`sessionStorage`-guarded, so remounts within the same session
 * don't re-fire it).
 *
 * All flags are plain per-viewer conveniences (browser storage guidance: never used for anything
 * requiring reliability across devices or for content itself) -- if storage is unavailable
 * (private browsing, cleared site data), this degrades to treating every visit as first-time,
 * which is the safe direction for a one-shot top-of-funnel signal.
 */
export function trackFenzSafeEntry(): void {
  try {
    const hasVisitedBefore = window.localStorage.getItem(RETURNING_VISITOR_KEY) === 'true';
    if (!hasVisitedBefore) {
      const cameFromGuestEntry = window.localStorage.getItem(GUEST_STARTED_KEY) === 'true';
      window.localStorage.setItem(RETURNING_VISITOR_KEY, 'true');
      if (!cameFromGuestEntry) trackFenzSafeFunnelEvent({milestone: 'organizer_started'});
      trackFenzSafeFunnelEvent({milestone: 'email_captured'});
      return;
    }
    if (window.sessionStorage.getItem(SESSION_RETENTION_FIRED_KEY) === 'true') return;
    window.sessionStorage.setItem(SESSION_RETENTION_FIRED_KEY, 'true');
    trackFenzSafeFunnelEvent({milestone: 'retained_user'});
  } catch {
    trackFenzSafeFunnelEvent({milestone: 'organizer_started'});
    trackFenzSafeFunnelEvent({milestone: 'email_captured'});
  }
}
